/**
 * Public Showcase Gallery
 * Displays content created with TripleTC
 * Features "Made with TripleTC" watermarks and partner attributions
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Image, Video, FileText, Code, Sparkles, 
  Heart, ArrowRight, Filter,
  Building2, Users, Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Showcase categories
const CATEGORIES = [
  { id: 'all', name: 'All', icon: Sparkles },
  { id: 'content', name: 'Content', icon: FileText },
  { id: 'images', name: 'Images', icon: Image },
  { id: 'videos', name: 'Videos', icon: Video },
  { id: 'code', name: 'Code & Apps', icon: Code },
  { id: 'charity', name: 'For Good', icon: Heart },
];

// Partner badges - COMING SOON (hidden until partnership confirmed)
const PARTNER_BADGES = {
  // Partnerships will be added here when confirmed
  // 'partner-id': { name, badge, color, url }
};

// Sample showcase items (will be replaced with DB items)
const SAMPLE_ITEMS = [
  {
    id: '1',
    title: 'SaaS Landing Page Copy',
    type: 'content',
    preview: 'Complete landing page copy for a project management tool with headlines, features, and CTA sections...',
    likes: 24,
    created_at: new Date().toISOString(),
  },
  {
    id: '2', 
    title: 'Product Launch Email Sequence',
    type: 'content',
    preview: '5-part email sequence for launching a new digital product to an existing audience...',
    likes: 18,
    created_at: new Date().toISOString(),
  },
  {
    id: '3',
    title: 'Freelance Proposal Template',
    type: 'content',
    preview: 'Winning proposal template for Upwork web development projects...',
    likes: 31,
    created_at: new Date().toISOString(),
  },
  {
    id: '4',
    title: 'E-commerce Product Photos',
    type: 'images',
    preview: 'AI-generated product mockups for an online jewelry store...',
    likes: 45,
    created_at: new Date().toISOString(),
  },
  {
    id: '5',
    title: 'Resume - Software Engineer',
    type: 'content',
    preview: 'ATS-optimized resume that landed 3 interviews in one week...',
    likes: 67,
    created_at: new Date().toISOString(),
  },
  {
    id: '6',
    title: 'Brand Explainer Video Script',
    type: 'videos',
    preview: '60-second script for a startup explainer video with scene breakdowns...',
    likes: 29,
    created_at: new Date().toISOString(),
  },
];

export default function Showcase() {
  const navigate = useNavigate();
  const [items, setItems] = useState(SAMPLE_ITEMS);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchShowcaseItems();
  }, [selectedCategory]);

  const fetchShowcaseItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/showcase`, {
        params: { category: selectedCategory !== 'all' ? selectedCategory : undefined }
      });
      if (response.data.items?.length > 0) {
        setItems(response.data.items);
      }
    } catch (err) {
      // Use sample items if API fails
      console.log('Using sample showcase items');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = selectedCategory === 'all' 
    ? items 
    : items.filter(item => item.type === selectedCategory || (selectedCategory === 'charity' && item.partner));

  const getTypeIcon = (type) => {
    switch(type) {
      case 'content': return FileText;
      case 'images': return Image;
      case 'videos': return Video;
      case 'code': return Code;
      default: return Sparkles;
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-teal-500/10 to-green-500/10 border border-teal-500/20 mb-6">
            <Sparkles className="w-4 h-4 text-teal-400" />
            <span className="text-sm font-medium text-teal-400">Community Showcase</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Made with <span className="bg-gradient-to-r from-teal-400 to-green-400 bg-clip-text text-transparent">TripleTC</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            See what creators, businesses, and changemakers are building with AI
          </p>
        </motion.div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-4 mb-12 max-w-2xl mx-auto"
        >
          <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-white">150+</div>
            <div className="text-zinc-500 text-sm">Creations</div>
          </div>
          <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-white">50+</div>
            <div className="text-zinc-500 text-sm">Creators</div>
          </div>
          <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-white">6</div>
            <div className="text-zinc-500 text-sm">Categories</div>
          </div>
        </motion.div>

        {/* Coming Soon Teaser */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-12"
        >
          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <span className="text-purple-400 font-semibold">Something Big is Coming</span>
              <Sparkles className="w-5 h-5 text-purple-400" />
            </div>
            <p className="text-zinc-400 text-sm">We're working on partnerships that will change how you create. Stay tuned.</p>
          </div>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap justify-center gap-2 mb-8"
        >
          {CATEGORIES.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  selectedCategory === category.id
                    ? 'bg-teal-500 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{category.name}</span>
              </button>
            );
          })}
        </motion.div>

        {/* Showcase Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item, index) => {
            const TypeIcon = getTypeIcon(item.type);
            const partner = item.partner ? PARTNER_BADGES[item.partner] : null;
            
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                className="group bg-zinc-900/50 border border-white/10 rounded-2xl overflow-hidden hover:border-teal-500/30 transition-all"
              >
                {/* Preview Area */}
                <div className="aspect-video bg-zinc-800 relative flex items-center justify-center">
                  <TypeIcon className="w-12 h-12 text-zinc-600" />
                  
                  {/* Made with TripleTC Watermark */}
                  <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-md">
                    <div className="w-4 h-4 bg-gradient-to-br from-teal-500 to-green-500 rounded text-[8px] font-bold text-white flex items-center justify-center">TC</div>
                    <span className="text-[10px] text-white/80">Made with TripleTC</span>
                  </div>

                  {/* Partner Badge */}
                  {partner && (
                    <div className={`absolute top-2 left-2 flex items-center gap-1 bg-gradient-to-r ${partner.color} px-2 py-1 rounded-md`}>
                      <Heart className="w-3 h-3 text-white" />
                      <span className="text-[10px] text-white font-medium">{partner.badge}</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="text-white font-semibold mb-2 group-hover:text-teal-400 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-zinc-500 text-sm line-clamp-2 mb-4">
                    {item.preview}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-zinc-500 text-sm">
                      <Heart className="w-4 h-4" />
                      <span>{item.likes}</span>
                    </div>
                    <button className="text-teal-400 hover:text-teal-300 text-sm font-medium flex items-center gap-1">
                      View <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center mt-16"
        >
          <h2 className="text-2xl font-bold text-white mb-4">Create Your Own</h2>
          <p className="text-zinc-400 mb-6">Join the community. Build something amazing.</p>
          <Button
            onClick={() => navigate('/templates')}
            className="bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-400 hover:to-green-400 text-white font-semibold rounded-full px-8"
          >
            Start Creating
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
